import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleService } from 'app/core/roles/role.service';
import { SnackbarService } from 'app/core/snackbars/snackbar.service';
import {
    NOTIFICATION_SENDER_ROLES,
    NotificationColleague,
    Notification,
    NotificationInbox,
    NotificationLab,
    SendNotification,
    SendNotificationResult,
    SentNotificationPage,
} from 'app/layout/common/notifications/notifications.types';
import {
    catchError,
    exhaustMap,
    filter,
    fromEvent,
    interval,
    map,
    merge,
    Observable,
    of,
    OperatorFunction,
    ReplaySubject,
    tap,
    throwError,
} from 'rxjs';

// How many of the latest notifications the bell panel shows, and how often it re-checks.
const PANEL_SIZE = 50;
const POLL_INTERVAL_MS = 60_000;

// Employee notifications (PCS.Web EmployeeNotificationController). Keeps the bell panel's inbox
// cached; the Notifications page pages through the API directly and calls refresh() after changes.
@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _inbox: ReplaySubject<NotificationInbox> = new ReplaySubject<NotificationInbox>(1);
    private _loaded = false;

    constructor(
        private _authService: AuthService,
        private _httpClient: HttpClient,
        private _roleService: RoleService,
        private _snackbarService: SnackbarService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Whether the signed-in user can write notifications: lab employees, and Dankospark support staff
     * (who pick a lab to write to). The server enforces the same rule.
     */
    get canSend(): boolean {
        return (
            !!this._authService.labId ||
            this._roleService.isInRole(NOTIFICATION_SENDER_ROLES) ||
            this.isSupportUser
        );
    }

    get isSupportUser(): boolean {
        return this._authService.isSupportUser;
    }

    get notifications$(): Observable<Notification[]> {
        return this._inbox.pipe(map((inbox) => inbox.items));
    }

    get unreadCount$(): Observable<number> {
        return this._inbox.pipe(map((inbox) => inbox.unreadCount));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load the latest notifications for the bell panel. Never errors: it runs in the app's initial
     * data resolver, where a failure would block the whole shell, and on every poll.
     */
    getAll(): Observable<Notification[]> {
        return this._httpClient
            .get<NotificationInbox>('apia/employeenotification/getMine', { params: { take: PANEL_SIZE } })
            .pipe(
                catchError(() => of(null)),
                tap((inbox) => {
                    if (inbox) {
                        this._loaded = true;
                        this._inbox.next(inbox);
                    } else if (!this._loaded) {
                        // Keep the last good list on a failed poll; only start empty.
                        this._inbox.next({ items: [], unreadCount: 0, total: 0 });
                    }
                }),
                map((inbox) => inbox?.items ?? []),
            );
    }

    refresh(): void {
        this.getAll().subscribe();
    }

    /**
     * Re-check every POLL_INTERVAL_MS while the browser tab is visible, and as soon as it becomes
     * visible again. Subscribe for as long as the bell is on screen.
     */
    poll(): Observable<Notification[]> {
        return merge(interval(POLL_INTERVAL_MS), fromEvent(document, 'visibilitychange')).pipe(
            filter(() => document.visibilityState === 'visible'),
            exhaustMap(() => this.getAll()),
        );
    }

    getInbox(skip: number, take: number, unreadOnly: boolean): Observable<NotificationInbox> {
        return this._httpClient
            .get<NotificationInbox>('apia/employeenotification/getMine', { params: { skip, take, unreadOnly } })
            .pipe(this._showError());
    }

    getSent(skip: number, take: number): Observable<SentNotificationPage> {
        return this._httpClient
            .get<SentNotificationPage>('apia/employeenotification/getSent', { params: { skip, take } })
            .pipe(this._showError());
    }

    getLabs(): Observable<NotificationLab[]> {
        return this._httpClient
            .get<NotificationLab[]>('apia/employeenotification/getLabs')
            .pipe(this._showError());
    }

    /**
     * Colleagues the user can write to - in the given lab (Dankospark staff only), else their own
     */
    getRecipients(labId?: string | null): Observable<NotificationColleague[]> {
        return this._httpClient
            .get<NotificationColleague[]>('apia/employeenotification/getRecipients', {
                params: labId ? { labId } : {},
            })
            .pipe(this._showError());
    }

    send(notification: SendNotification): Observable<SendNotificationResult> {
        return this._httpClient
            .post<SendNotificationResult>('apia/employeenotification/send', notification)
            .pipe(this._showError());
    }

    /**
     * Reply to the sender of a notification I received (marks it read too)
     */
    reply(notification: Notification, message: string, link: string | null): Observable<SendNotificationResult> {
        return this._httpClient
            .post<SendNotificationResult>('apia/employeenotification/reply', { id: notification.id, message, link })
            .pipe(this._showError(), tap(() => this.refresh()));
    }

    setRead(notification: Notification, read: boolean): Observable<boolean> {
        return this._httpClient
            .post<boolean>('apia/employeenotification/setRead', { id: notification.id, read })
            .pipe(this._showError(), tap(() => this.refresh()));
    }

    markAllAsRead(): Observable<number> {
        return this._httpClient
            .post<number>('apia/employeenotification/markAllRead', {})
            .pipe(this._showError(), tap(() => this.refresh()));
    }

    delete(notification: Notification): Observable<boolean> {
        return this._httpClient
            .post<boolean>('apia/employeenotification/remove', { id: notification.id })
            .pipe(this._showError(), tap(() => this.refresh()));
    }

    deleteSent(notificationId: string): Observable<boolean> {
        return this._httpClient
            .post<boolean>('apia/employeenotification/removeSent', { id: notificationId })
            .pipe(this._showError());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _showError<T>(): OperatorFunction<T, T> {
        return catchError((err: Error) => {
            this._snackbarService.error(err.message);
            return throwError(() => err);
        });
    }
}
